import { useState } from "react";
import { PageCollectionLayout } from "../../../../../components/pageCollection/PageCollectionLayout";
import { useMediaQuery } from "usehooks-ts";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/shallow";
import { formatCurrency } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { Settings } from "../../../constants/tabs";
import { tabsLeft } from "../../../constants/tabs.shared";
import {
  SellarCreditStatementSortType,
  SellarCreditStatementSortValues,
} from "../../../stores/SellarCreditStatementTypes";
import { useSellarCreditStatementStore } from "../../../stores/SellarCreditStatementStore";
import { SellarCreditStatementTable } from "./components/SellarCreditStatementTable";
import { SellarCreditStatementCardList } from "./components/SellarCreditStatementCardList";
import { useStatementList } from "../../../../../hooks/api/v2/sellarCreditManagement/useStatementList";
import { SellarCreditStatementFilter } from "./components/SellarCreditStatementFilter";
import { capitalize } from "@sellar-io/common";
import { SellarCreditStatementStats } from "./components/SellarCreditStatementStats";
import { TableHeader } from "../../../../../components/table/TableHeader";
import { SELLAR_CREDIT_STATEMENT } from "../../../../../constants/routes";
import { withSearchParams } from "../../../../../utils/withSearchParams";
import { SwitchCreditProviderButton } from "../../../components/SwitchCreditProviderButton";
import { useNavigate } from "react-router";
import { Tag } from "../../../../../components/tag/Tag";
import { DataButton, Icon, InputText } from "@sellar-io/components";
import { useTwoBillingPeriodSummary } from "../../../../../hooks/api/v2/sellarCreditManagement/useTwoBillingPeriodSummary";
import { Alert } from "../../../../../components/alert/Alert";
import { SpinnerLegacy } from "../../../../../components";
import { useLegalEntityId } from "../../../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { useLegalEntity } from "../../../../../hooks/api/v2/legalEntity/useLegalEntity";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";

export const TwoSellarCreditStatement = () => {
  const [desktopFilter, setDesktopFilter] = useState(false);
  const [mobileFilter, setMobileFilter] = useState(false);
  const [clickOutside, setClickOutside] = useState(true);
  const scPaymentAllocationHistoryEnabled = useFeatureFlagStore().isActive(
    "SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY",
  );

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const state = useSellarCreditStatementStore(useShallow((state) => state));

  const storeActions = state;

  const handleTableSort = (
    columnSort: SellarCreditStatementSortType | null,
  ) => {
    storeActions.setColumnSort(columnSort ?? "createdAt_desc");
  };

  const [debouncedSearchValue] = useDebounce(state.search, 500);

  const documentTotalMinNum = parseInt(state.documentTotalMin);
  const documentTotalMaxNum = parseInt(state.documentTotalMax);

  const documentYear = dayjs(state.documentMonth, "YYYY-MM").year();
  const documentMonth = dayjs(state.documentMonth, "YYYY-MM").month(); // 0-jan 11-dec

  const twoBillingPeriodQueryParams = {
    query: [
      {
        type: "month" as const,
        modifier: "EQUALS" as const,
        value: documentMonth,
      },
      {
        type: "year" as const,
        modifier: "EQUALS" as const,
        value: documentYear,
      },
    ],
  };

  const {
    data: twoBillingPeriod,
    isPending: twoBillingPeriodPending,
    isError: twoBillingPeriodError,
  } = useTwoBillingPeriodSummary(twoBillingPeriodQueryParams);

  const twoBillingPeriodStartDate = twoBillingPeriod?.startDate;
  const twoBillingPeriodEndDate = twoBillingPeriod?.endDate;
  const twoStatementIssueDate = twoBillingPeriod?.issueDate;

  const queryParams = {
    search: debouncedSearchValue,
    sort: SellarCreditStatementSortValues[state.columnSort]
      ? [SellarCreditStatementSortValues[state.columnSort].mutation]
      : [],
    query: [
      ...(state.business
        ? [
            {
              type: "businessId",
              modifier: "EQUALS",
              value: state.business.id,
            } as const,
          ]
        : []),
      ...(state.user
        ? [
            {
              type: "userId",
              modifier: "EQUALS",
              value: state.user.id,
            } as const,
          ]
        : []),
      ...(state.paymentStatuses.length > 0
        ? [
            {
              type: "paymentStatus",
              modifier: "IN",
              value: state.paymentStatuses,
            } as const,
          ]
        : []),
      ...(!!state.documentTotalMin || !!state.documentTotalMax
        ? [
            {
              type: "documentTotalValue",
              modifier: "BETWEEN",
              value: {
                low: isNaN(documentTotalMinNum) ? 0 : documentTotalMinNum * 100,
                high: isNaN(documentTotalMaxNum)
                  ? 999999999
                  : documentTotalMaxNum * 100,
              },
            } as const,
          ]
        : []),
      ...(!!state.documentMonth
        ? [
            {
              type: "documentDate",
              modifier: "BETWEEN",
              value: {
                low: dayjs(state.documentMonth).startOf("month").toDate(),
                high: dayjs(state.documentMonth).endOf("month").toDate(),
              },
            } as const,
          ]
        : []),
      ...(twoBillingPeriodStartDate &&
      twoBillingPeriodEndDate &&
      twoStatementIssueDate
        ? [
            {
              type: "billingPeriodStartDate",
              modifier: "EQUALS",
              value: dayjs(twoBillingPeriodStartDate).toDate(),
            } as const,
            {
              type: "billingPeriodEndDate",
              modifier: "EQUALS",
              value: dayjs(twoBillingPeriodEndDate).toDate(),
            } as const,
            {
              type: "statementIssueDate",
              modifier: "EQUALS",
              value: dayjs(twoStatementIssueDate).toDate(),
            } as const,
          ]
        : []),
      ...(!!state.excludeCreditNotes
        ? [
            {
              type: "excludeCreditNotes",
              modifier: "EQUALS",
              value: state.excludeCreditNotes,
            } as const,
          ]
        : []),
    ],
  };

  const invoiceQuery = useStatementList(queryParams, {
    refetchOnWindowFocus: false,
  });

  const flatData =
    invoiceQuery.data?.pages.flatMap((page) => page.map((row) => row)) ?? [];

  const filteredTotalOutstanding = flatData.reduce((prev, invoice) => {
    if (invoice.type === "INV" && invoice.status !== "void") {
      return prev + invoice.total;
    }
    return prev;
  }, 0);

  const filtersApplied =
    state.business !== null ||
    state.user !== null ||
    state.documentTotalMin !== "" ||
    state.documentTotalMax !== "" ||
    state.excludeCreditNotes ||
    state.paymentStatuses.length > 0;

  const changeMonth = (forward: boolean) => {
    storeActions.setDocumentMonth(
      dayjs(state.documentMonth)
        .add(forward ? 1 : -1, "months")
        .format("YYYY-MM"),
    );
  };

  const navigate = useNavigate();

  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });

  const companyHasHokodoAccount = !!legalEntity?.hokodoCompanyId;

  if (twoBillingPeriodError || invoiceQuery.isError) {
    return (
      <Alert
        type="error"
        title={`Error getting sellar credit statement information`}
        message={
          "Please refresh the page and try again. If the problem persists then reach out to support."
        }
      />
    );
  }

  return (
    <PageCollectionLayout
      title="Sellar Credit"
      topComponent={
        companyHasHokodoAccount && (
          <SwitchCreditProviderButton
            onSwitchClick={() =>
              navigate(
                withSearchParams(SELLAR_CREDIT_STATEMENT, {
                  provider: "hokodo",
                }),
              )
            }
            currentProvider="two"
          />
        )
      }
      tabsLeft={tabsLeft({
        provider: "two",
        scPaymentAllocationHistoryEnabled,
      })}
      rightComponent={<Settings provider="two" />}
    >
      <div
        className={twMerge(
          "container mx-auto flex max-w-[1300px] flex-col bg-zinc-100",
          isDesktop ? "mt-6" : "mt-0",
        )}
      >
        {twoBillingPeriodPending ? (
          <SpinnerLegacy />
        ) : (
          <SellarCreditStatementStats
            statementMonth={dayjs(state.documentMonth).toDate()}
            billingPeriodSummary={twoBillingPeriod}
          />
        )}

        <TableHeader
          bottomRightComponent={
            filtersApplied && (
              <>
                <span className="font-semibold">
                  Total with filter(s) applied:
                </span>{" "}
                {formatCurrency(filteredTotalOutstanding)}
              </>
            )
          }
          className="mt-4"
          clickOutside={clickOutside}
          desktopFilterActive={desktopFilter}
          setDesktopFilterActive={setDesktopFilter}
          filter={
            <SellarCreditStatementFilter
              onDismiss={() => {
                setDesktopFilter(false);
                setMobileFilter(false);
              }}
              setClickOutside={(value?: boolean) =>
                setClickOutside(value ?? !clickOutside)
              }
            />
          }
          mobileFilterActive={mobileFilter}
          setMobileFilterActive={setMobileFilter}
          rightComponent={
            <>
              <DataButton
                onClick={() => changeMonth(false)}
                border={true}
                className="px-1"
                disabled={state.documentMonth === "2025-02"}
              >
                <Icon icon="CHEVRON_LEFT" />
              </DataButton>

              <InputText
                value={state.documentMonth}
                onChange={(e) => storeActions.setDocumentMonth(e.target.value)}
                min="2025-02"
                type="month"
                className="relative flex w-fit cursor-pointer items-center justify-center gap-2 rounded-lg border border-solid border-zinc-300 bg-white px-3 py-2 text-center text-sm leading-5 font-semibold tracking-wide whitespace-nowrap not-italic shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] transition-colors first-of-type:ml-0 last-of-type:mr-0 hover:bg-zinc-100 motion-reduce:transition-none"
              />

              <DataButton
                onClick={() => changeMonth(true)}
                border={true}
                className="px-1"
                disabled={state.documentMonth === dayjs().format("YYYY-MM")}
              >
                <Icon icon="CHEVRON_RIGHT" />
              </DataButton>
            </>
          }
          search={state.search}
          searchPlaceholder="Search #, supplier, business, user..."
          setSearch={storeActions.setSearch}
          tags={
            <>
              {!!state.business && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setBusiness(null)}
                  icon="FACTORY"
                >
                  {state.business.name}
                </Tag>
              )}
              {!!state.user && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setUser(null)}
                  icon="PROFILE"
                >
                  {state.user.name}
                </Tag>
              )}
              {state.paymentStatuses.length > 0 && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setPaymentStatuses([])}
                  icon="FILE_DOCUMENT_CHECK_OUTLINE"
                >
                  {state.paymentStatuses
                    .map((status) => capitalize(status))
                    .join(", ")}
                </Tag>
              )}
              {(!!state.documentTotalMin || !!state.documentTotalMax) && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => {
                    storeActions.setDocumentTotalMin("");
                    storeActions.setDocumentTotalMax("");
                  }}
                  icon="CART"
                >
                  {`Document total ${state.documentTotalMin ? formatCurrency(parseInt(state.documentTotalMin) * 100) : "up to"} ${!!state.documentTotalMax && !!state.documentTotalMin && state.documentTotalMin !== "" ? "-" : !!state.documentTotalMin && !state.documentTotalMax ? "+" : ""} ${!!state.documentTotalMax ? formatCurrency(parseInt(state.documentTotalMax) * 100) : ""}`}
                </Tag>
              )}
              {!!state.excludeCreditNotes && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setExcludeCreditNotes(false)}
                  icon="NOTE_OFF_OUTLINE"
                >
                  Exclude credit notes
                </Tag>
              )}
            </>
          }
        />
        {isDesktop ? (
          <SellarCreditStatementTable
            handleTableSort={handleTableSort}
            query={invoiceQuery}
          />
        ) : (
          <SellarCreditStatementCardList query={invoiceQuery} />
        )}
      </div>
    </PageCollectionLayout>
  );
};
